import { Box, MenuItem, TextField } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import {
  ActionModerationStatus,
  ModerationAction,
  acceptAction,
  getActionsModeration,
  rejectAction,
} from "src/apis/action";
import CompanySelect from "src/pages/components/CompanySelect";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import { Id } from "src/utils/types";
import ActionsTable from "./components/ActionsTable";
import RejectActionDialog from "./components/RejectActionDialog";

type StatusFilter = ActionModerationStatus | "all";

const hasCompanyFilter = (id: Id): boolean => Boolean(id) && String(id) !== "-1";

const ManageActions: FC = (): JSX.Element => {
  const [actions, setActions] = useState<ModerationAction[]>([]);
  const [status, setStatus] = useState<StatusFilter>("pending");
  const [companyId, setCompanyId] = useState<Id>("");
  const [error, setError] = useState("");
  const [rejectTarget, setRejectTarget] = useState<ModerationAction | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getActionsModeration({
        status: status === "all" ? "" : status,
        company_id: hasCompanyFilter(companyId) ? companyId : undefined,
      });
      setActions(data);
      setError("");
    } catch (err: any) {
      setError(err?.message ?? "Erro ao carregar as ações.");
    }
  }, [status, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAccept = async (item: ModerationAction) => {
    try {
      await acceptAction(item.id);
      await load();
    } catch (err: any) {
      setError(err?.message ?? "Erro ao aceitar a ação.");
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    try {
      await rejectAction(rejectTarget.id, reason);
      await load();
    } catch (err: any) {
      setError(err?.message ?? "Erro ao rejeitar a ação.");
    }
  };

  return (
    <PageContainer
      title="Gerenciar ações dos psicólogos"
      error={error}
      guard={isUserAdministrator}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="pending">Pendentes</MenuItem>
          <MenuItem value="accepted">Aceitas</MenuItem>
          <MenuItem value="rejected">Rejeitadas</MenuItem>
          <MenuItem value="all">Todas</MenuItem>
        </TextField>

        <CompanySelect
          isFilter
          companyId={companyId}
          setCompanyId={setCompanyId}
          setError={setError}
          useAllOption
        />
      </Box>

      <ActionsTable
        actions={actions}
        onAccept={handleAccept}
        onReject={(item) => setRejectTarget(item)}
      />

      <RejectActionDialog
        open={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
      />
    </PageContainer>
  );
};

export default ManageActions;
